// ─── Constants ────────────────────────────────────────────────────────────────

export const CACHE_TTL        = 10 * 60 * 1000; // 10 minutes — how long entries stay valid
export const CLEAN_INTERVAL   = 15 * 60 * 1000; // 15 minutes — min time between sweeps
export const CACHE_KEY_PREFIX = 'nxs_cache:';   // all sessionStorage keys owned by this cache

const _memoryCache = new Map(); // fast tab-local cache for repeated reads

// Storage format: '{"ts":1717200000000,"data":{payload}}'
// Positions:        0123456 = prefix "{"ts":" (6 chars), then 13-digit epoch, then ',"data":' (8 chars)
const TS_OFFSET  = 6;            // length of '{"ts":'
const TS_END     = 19;           // TS_OFFSET + TS_LENGTH (6 + 13)
const DATA_START = 27;           // TS_END + length of ',"data":' (19 + 8)
const CLEAN_KEY  = 'nxs_cache:__last_clean'; // reserved key — stores last sweep timestamp

// ─── Cache bypass params — dashboard denylist ────────────────────────────────

/**
 * Dashboard request body keys that indicate a live, non-cacheable call.
 * If ANY key in the request is present in this set, the request bypasses cache.
 *
 * Default is CACHE. Only free-text inputs, arbitrary numeric/date ranges, and
 * non-enumerable single filters are listed here.
 *
 * Stable params (dropdowns, pagination, scoping IDs) are NOT listed and will
 * naturally form part of the cache key via _buildQueryString.
 *
 * Explicit cache: false at the call site (e.g. fetchHydrateStatus, requestHydration)
 * overrides this entirely — no need to list those types here.
 */
export const CACHE_BYPASS_PARAMS = new Set([
    // Free-text inputs
    'search',
    'directory_contains',
    'name_contains',
    'name_ends_with',
    'name_starts_with',
    'path_contains',
    'path_ends_with',
    'path_starts_with',
    'attribute_search',
    'attribute_filter',
    'volser_starts_with',
    // Numeric range inputs
    'size_minimum',
    'size_maximum',
    // Date range inputs
    'date_from', 'date_to',
    'backed_up_since', 'backed_up_until',
    // Non-enumerable single filter
    'backup_source_id',
]);

// ─── Invalidation map — Django mutation → list prefix ─────────────────────────

/**
 * Maps DELETE (and implicitly POST) path patterns to the list cache prefix that
 * must be invalidated. Each entry has:
 *   pattern — regex matching the full request path
 *   prefix  — function(match) returning the nxs_cache: prefix to wipe
 *
 * POST paths don't need the map — the path IS the list path.
 * DELETE paths need it to derive the parent list prefix from a detail path.
 * Every case is explicitly listed — no automatic derivation.
 */
export const CACHE_INVALIDATION_MAP = [
    { pattern: /^\/jobs\/[^/]+\/$/,
      prefix: ()  => '/jobs/' },
    { pattern: /^\/customers\/[^/]+\/$/,
      prefix: ()  => '/customers/' },
    { pattern: /^\/jobs\/([^/]+)\/shipments\/[^/]+\/$/,
      prefix: (m) => `/jobs/${m[1]}/shipments/` },
    { pattern: /^\/jobs\/([^/]+)\/fees\/[^/]+\/$/,
      prefix: (m) => `/jobs/${m[1]}/fees/` },
    { pattern: /^\/jobs\/([^/]+)\/tapes\/[^/]+\/$/,
      prefix: (m) => `/jobs/${m[1]}/tapes/` },
    { pattern: /^\/jobs\/([^/]+)\/documents\/[^/]+\/$/,
      prefix: (m) => `/jobs/${m[1]}/documents/` },
    { pattern: /^\/customers\/([^/]+)\/users\/[^/]+\/$/,
      prefix: (m) => `/customers/${m[1]}/users/` },
    { pattern: /^\/customers\/([^/]+)\/users\/[^/]+\/user\/$/,
      prefix: (m) => `/customers/${m[1]}/users/` },
];

// ─── Passive cleaner ─────────────────────────────────────────────────────────

/**
 * Sweeps all nxs_cache: entries and removes expired ones.
 * Called by cacheGet/cacheSet — fires at most once per CLEAN_INTERVAL.
 * Last-clean timestamp stored in sessionStorage so it survives page refresh.
 */
function _purgeExpiredCacheIfDue() {
    const lastClean = Number(sessionStorage.getItem(CLEAN_KEY) || 0);
    if (Date.now() - lastClean < CLEAN_INTERVAL) return;

    Object.keys(sessionStorage)
        .filter(k => k.startsWith(CACHE_KEY_PREFIX) && k !== CLEAN_KEY)
        .forEach(k => {
            const entry = sessionStorage.getItem(k);
            if (!entry) return;
            const ts = Number(entry.slice(TS_OFFSET, TS_END));
            if (!ts || Date.now() - ts >= CACHE_TTL) sessionStorage.removeItem(k);
        });

    try { sessionStorage.setItem(CLEAN_KEY, String(Date.now())); } catch {}
}

// ─── Cache primitives ─────────────────────────────────────────────────────────

export function cacheGet(key, ttl = CACHE_TTL) {
    const now = Date.now();
    const cached = _memoryCache.get(key);
    if (cached && now - cached.ts < ttl) {
        // Keep the in-memory cache consistent with sessionStorage. If the underlying
        // sessionStorage entry has been removed externally, the cached value is stale.
        if (sessionStorage.getItem(CACHE_KEY_PREFIX + key) === null) {
            _memoryCache.delete(key);
        } else {
            return cached.data;
        }
    }

    _purgeExpiredCacheIfDue();
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
    if (!raw) return null;
    const ts = Number(raw.slice(TS_OFFSET, TS_END));
    if (Number.isNaN(ts) || now - ts >= ttl) {
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
        _memoryCache.delete(key);
        return null;
    }
    try {
        const data = JSON.parse(raw.slice(DATA_START, -1));
        _memoryCache.set(key, {ts, data});
        return data;
    } catch {
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
        _memoryCache.delete(key);
        return null;
    }
}

export function cacheSet(key, data) {
    const now = Date.now();
    _memoryCache.set(key, {ts: now, data});
    _purgeExpiredCacheIfDue();
    try {
        sessionStorage.setItem(CACHE_KEY_PREFIX + key, '{"ts":' + now + ',"data":' + JSON.stringify(data) + '}');
    } catch {}
}

export function cacheInvalidatePrefix(prefix) {
    const full = CACHE_KEY_PREFIX + prefix;
    Object.keys(sessionStorage)
        .filter(k => k.startsWith(full))
        .forEach(k => sessionStorage.removeItem(k));

    const normalizedPrefix = prefix;
    Array.from(_memoryCache.keys())
        .filter(k => k.startsWith(normalizedPrefix))
        .forEach(k => _memoryCache.delete(k));
}

// ─── Query string builder ─────────────────────────────────────────────────────

/**
 * Builds a stable, normalised query string from a params object.
 *
 * Rules:
 *   - null, undefined, "", and [] are stripped (key omitted entirely)
 *   - 0 and false are preserved (valid param values)
 *   - Keys are sorted alphabetically (A→Z)
 *   - Array values: empty-like items stripped, remaining values sorted A→Z,
 *     expanded to repeated key pairs in alphabetical position among all keys
 *   - All keys and values are URI-encoded via encodeURIComponent
 *
 * Used for both URL construction (Django) and cache key param segments (Dashboard).
 *
 * @param {object} params
 * @returns {string} "?k=v&k2=v2" or "" if no valid params remain
 */
export function _buildQueryString(params = {}) {
    const parts = [];

    Object.keys(params)
        .sort((a, b) => a.localeCompare(b))
        .forEach(k => {
            const v = params[k];
            if (Array.isArray(v)) {
                const clean = v
                    .filter(item => item !== null && item !== undefined && item !== '')
                    .map(String)
                    .sort((a, b) => a.localeCompare(b));
                clean.forEach(item =>
                    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(item)}`)
                );
            } else {
                if (v !== null && v !== undefined && v !== '') {
                    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
                }
            }
        });

    return parts.length ? '?' + parts.join('&') : '';
}
